import { ForwardedRef, forwardRef, useState } from "react";
import { Control, useForm } from "react-hook-form";
import { FiEye, FiEyeOff, FiInfo } from "react-icons/fi";

export const BasicInputField = forwardRef(
	(props: JSX.IntrinsicElements["input"], ref?: ForwardedRef<HTMLInputElement>) => (
		<input
			{...props}
			className={`flex-shrink-0 flex-grow border-none border-transparent bg-gray-50 p-2 focus:outline-none focus:ring-0 ${
				props.className ?? ""
			}`}
			ref={ref}
		/>
	),
);

BasicInputField.displayName = "BasicInputField";

const InputFieldWithoutRef = (
	{
		type = "text",
		className,
		errorMessage,
		label,
		placeholder,
		name,
		showPasswordIcon,
		helpText,
		omitMarginBottomOnError,
		customGrayOnDisable,
		customWidthOfInputTag,
		noBorderRadius,
		showArrowsOnNumberField = false,
		smallNumberInputVariant,
		...otherProps
	}: {
		type?: HTMLInputElement["type"];
		className?: string;
		errorMessage?: string;
		helpText?: string;
		label?: string;
		placeholder?: string;
		control?: Control<any>;
		showPasswordIcon?: boolean;
		omitMarginBottomOnError?: boolean;
		customGrayOnDisable?: boolean;
		customWidthOfInputTag?: string;
		noBorderRadius?: boolean;
		showArrowsOnNumberField?: boolean;
		smallNumberInputVariant?: boolean;
	} & ReturnType<ReturnType<typeof useForm>["register"]>,
	ref: ForwardedRef<HTMLInputElement>,
) => {
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const onPasswordVisibilityChange = () => setIsPasswordVisible((value) => !value);
	return (
		<div className={`w-full space-y-1 ${className} ${errorMessage && !omitMarginBottomOnError && "mb-5"}`}>
			{label && (
				<label htmlFor={name} className=" text-sm font-bold">
					{label}
				</label>
			)}
			{errorMessage && <p className="text-xs font-medium text-red-500">{errorMessage}</p>}
			{helpText && <p className="my-2 text-xs text-gray-500">{helpText}</p>}

			<div
				role="group"
				className={`flex w-full items-center rounded-md border bg-gray-50 px-2 focus:border-2 focus:border-blue-600 focus:outline-none focus:ring-2 ${className} ${
					errorMessage ? "border-red-500 focus:ring-red-500" : "border-gray-400 ring-secondary hover:ring-2"
				} ${otherProps.disabled && customGrayOnDisable ? "bg-warmGray-200" : ""} ${
					noBorderRadius ? "rounded-none" : ""
				} ${smallNumberInputVariant ? `!w-[120px]` : ""}`}
			>
				{errorMessage && <FiInfo className="text-red-500" />}
				<BasicInputField
					id={name}
					name={name}
					type={isPasswordVisible ? "text" : type}
					placeholder={placeholder}
					{...otherProps}
					className={`mt-1 flex-shrink-0 flex-grow border-none border-transparent bg-gray-50 p-2 focus:outline-none focus:ring-0 ${
						customGrayOnDisable ? "disabled:cursor-not-allowed disabled:bg-warmGray-200" : ""
					} ${customWidthOfInputTag ? customWidthOfInputTag : ""} ${showArrowsOnNumberField ? "show-spinner" : ""} ${
						smallNumberInputVariant ? `!w-[110px]` : ""
					}`}
					ref={ref}
				/>
				{showPasswordIcon && (
					<button type="button">
						{isPasswordVisible ? (
							<FiEyeOff className="text-gray-400" onClick={onPasswordVisibilityChange} />
						) : (
							<FiEye className="text-gray-400" onClick={onPasswordVisibilityChange} />
						)}
					</button>
				)}
			</div>
		</div>
	);
};

const InputField = forwardRef(InputFieldWithoutRef);

InputField.displayName = "InputField";
export default InputField;
